import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Download, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  downloadAccountantSubdiarioCsv,
  downloadAccountantSubdiarioPdf,
  getAccountantSubdiarioDetail,
} from "@/services/dashboardService";

const MONTH_LABELS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function periodLabel(period: string): string {
  const [year, month] = period.split("-");
  const idx = Number(month) - 1;
  return `${MONTH_LABELS_ES[idx] ?? month} ${year}`;
}

/** YYYY-MM-DD → DD/MM/YYYY (Uruguay numeric format), timezone-safe. */
function uyDate(iso: string): string {
  const [y, m, d] = (iso ?? "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/** UYU with thousands separators; blank when zero to mirror the DGI report. */
function money(n: number, blankZero = false): string {
  if (blankZero && n === 0) return "";
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function AccountantClientSubdiarioPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId, period } = useParams<{ tenantId: string; period: string }>();
  const [downloading, setDownloading] = useState<"csv" | "pdf" | null>(null);

  // Arriving from a month row leaves the cartera scroll position halfway down
  // the tenant detail page; bring the ledger into view. The scroll container is
  // AppLayout's <main>, not the window.
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const detailQuery = useQuery({
    queryKey: ["accountant", "tenant-subdiario", tenantId ?? "", period ?? ""],
    queryFn: () => getAccountantSubdiarioDetail(tenantId!, period!),
    enabled: Boolean(tenantId && period),
    staleTime: 60_000,
  });

  const handleDownload = async (format: "csv" | "pdf"): Promise<void> => {
    if (!tenantId || !period) return;
    setDownloading(format);
    try {
      if (format === "csv") {
        await downloadAccountantSubdiarioCsv(tenantId, period);
      } else {
        await downloadAccountantSubdiarioPdf(tenantId, period);
      }
    } catch {
      toast.error(
        t(
          "accountantSubdiario.downloadError",
          "No pudimos generar el archivo. Probá de nuevo.",
        ),
      );
    } finally {
      setDownloading(null);
    }
  };

  if (detailQuery.isLoading) return <LoadingPage />;

  const detail = detailQuery.data;
  if (!detail) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          eyebrow={t("accountantSubdiario.eyebrow", "Subdiario de Ventas")}
          title={period ? periodLabel(period) : ""}
          onBack={() => navigate(`/accountant/client/${tenantId}`)}
        />
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t(
              "accountantSubdiario.loadError",
              "No pudimos cargar el subdiario de este período.",
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, rows, totals, ivaBreakdown } = detail;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("accountantSubdiario.eyebrow", "Subdiario de Ventas")}
        title={periodLabel(detail.period)}
        description={t(
          "accountantSubdiario.description",
          "Montos en UYU. CFE en otra moneda convertidos al tipo de cambio de cada comprobante.",
        )}
        onBack={() => navigate(`/accountant/client/${tenantId}`)}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void handleDownload("csv")}
          disabled={downloading != null}
        >
          {downloading === "csv" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          <span className="ml-1.5">
            {t("accountantSubdiario.downloadCsv", "Descargar CSV")}
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void handleDownload("pdf")}
          disabled={downloading != null}
        >
          {downloading === "pdf" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          <span className="ml-1.5">
            {t("accountantSubdiario.downloadPdf", "Descargar PDF")}
          </span>
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-6 py-6">
          {/* Document header — company block, branded */}
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--fx-ink-100)] pb-4">
            <div>
              <h2 className="text-lg font-semibold text-[var(--fx-ink-900)]">
                {t("accountantSubdiario.docTitle", "Subdiario de Ventas")}
              </h2>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {uyDate(detail.from)} — {uyDate(detail.to)}
              </p>
            </div>
            <div className="text-right text-sm">
              <div className="font-medium text-[var(--fx-ink-900)]">
                {company.legalName?.trim() || company.name}
              </div>
              {company.address ? (
                <div className="text-muted-foreground">{company.address}</div>
              ) : null}
              {company.phone ? (
                <div className="text-muted-foreground">{company.phone}</div>
              ) : null}
              <div className="text-muted-foreground">RUC {company.rut}</div>
            </div>
          </div>

          {/* Per-CFE ledger */}
          {rows.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t(
                "accountantSubdiario.empty",
                "Sin comprobantes confirmados en este período.",
              )}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[1100px] text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("accountantSubdiario.col.emision", "Emisión")}</TableHead>
                    <TableHead>{t("accountantSubdiario.col.desc", "Descripción")}</TableHead>
                    <TableHead>{t("accountantSubdiario.col.serie", "Serie")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.numero", "Número")}</TableHead>
                    <TableHead>{t("accountantSubdiario.col.rut", "RUT")}</TableHead>
                    <TableHead>{t("accountantSubdiario.col.razon", "Razón Social")}</TableHead>
                    <TableHead>{t("accountantSubdiario.col.moneda", "Mon.")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.exento", "Exento")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.ivaMin", "IVA Mín")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.ivaBas", "IVA Básico")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.ivaSusp", "IVA Susp")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.neto", "Neto")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.iva", "IVA")}</TableHead>
                    <TableHead className="text-right">{t("accountantSubdiario.col.total", "Total")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.cfeType}-${r.serie}-${r.number ?? i}`}>
                      <TableCell className="whitespace-nowrap">{uyDate(r.issueDate)}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.cfeTypeLabel}</TableCell>
                      <TableCell>{r.serie}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.number ?? ""}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.receptorRut ?? ""}</TableCell>
                      <TableCell className="max-w-[180px] truncate">{r.receptorName}</TableCell>
                      <TableCell>{r.monedaOrigen}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.exentoUyu, true)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.ivaMinBaseUyu, true)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.ivaBasicoBaseUyu, true)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.ivaSuspensoBaseUyu, true)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.netoUyu)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(r.ivaUyu)}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums">{money(r.totalUyu)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 border-[var(--fx-ink-200)] bg-[var(--fx-paper-2)]/40 font-semibold">
                    <TableCell colSpan={6}>{t("accountantSubdiario.totalRow", "TOTAL")}</TableCell>
                    <TableCell>UYU</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.exentoUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.ivaMinBaseUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.ivaBasicoBaseUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.ivaSuspensoBaseUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.netoUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.ivaUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(totals.totalUyu)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {/* IVA-by-rate breakdown */}
          <div className="max-w-xl">
            <h3 className="mb-2 text-sm font-semibold text-[var(--fx-ink-900)]">
              {t("accountantSubdiario.breakdown.title", "Resumen por tasa de IVA")}
            </h3>
            <Table className="text-sm">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("accountantSubdiario.breakdown.concepto", "Concepto")}</TableHead>
                  <TableHead className="text-right">{t("accountantSubdiario.breakdown.tasa", "Tasa")}</TableHead>
                  <TableHead className="text-right">{t("accountantSubdiario.breakdown.neto", "Neto")}</TableHead>
                  <TableHead className="text-right">{t("accountantSubdiario.breakdown.iva", "IVA")}</TableHead>
                  <TableHead className="text-right">{t("accountantSubdiario.breakdown.total", "Total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ivaBreakdown.map((b) => (
                  <TableRow key={b.concepto}>
                    <TableCell className="font-semibold">{b.concepto}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {b.tasaPct > 0 ? `${b.tasaPct}%` : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(b.netoUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(b.ivaUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(b.totalUyu)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
